import { FormikProps } from 'formik';
import React from 'react';
import InputMask from 'react-input-mask';

import { toAbsoluteUrl } from '../../../../../_metronic/_helpers';
import useCreditCardFlagSVG from '../../../../hooks/useCreditCardFlagSVG';
import { useFieldError } from '../hooks/useFieldError';
import { PAYMENT_METHOD_OPTIONS } from './paymentMethodOptions';

import {
  Card,
  CardBrandIcon,
  CardNumberFieldWrapper,
  Divider,
  FormRow,
  PaymentMethodIcon,
  PaymentMethodOption,
  PaymentMethodOptions,
  SectionBlock,
  SectionHeaderRow,
  SectionHeading,
  SectionNumber,
} from '../styles';

import { IPaymentLinkFormValues } from '../types/types';
import {
  ApiPaymentMethod,
  PAYMENT_METHODS,
  PaymentMethod,
} from '../helpers/paymentMethods';

interface PaymentMethodSectionProps {
  form: FormikProps<IPaymentLinkFormValues>;
  paymentMethodSelected: PaymentMethod;
  onSelectPaymentMethod: (method: PaymentMethod) => void;
  allowedPaymentMethods: ApiPaymentMethod[];
}

export const PaymentMethodSection: React.FC<PaymentMethodSectionProps> = ({
  form,
  paymentMethodSelected,
  onSelectPaymentMethod,
  allowedPaymentMethods,
}) => {
  const { svg: cardBrandSvg } = useCreditCardFlagSVG({
    cardNumber: form.values.cardNumber,
  });

  const availableMethods = PAYMENT_METHOD_OPTIONS.filter(({ method }) =>
    allowedPaymentMethods.includes(PAYMENT_METHODS[method]),
  );

  const hasError = useFieldError(form);

  const showCardHolderNameError = hasError('cardHolderName');
  const showCardNumberError = hasError('cardNumber');
  const showCardExpirationError = hasError('cardExpiration');
  const showCardCvvError = hasError('cardCvv');

  return (
    <Card>
      <SectionHeaderRow>
        <SectionNumber>2</SectionNumber>
        <SectionHeading>Método de pagamento</SectionHeading>
      </SectionHeaderRow>

      <Divider />

      <PaymentMethodOptions>
        {availableMethods.map(({ method, label, icon: Icon }) => (
          <PaymentMethodOption
            key={method}
            type="button"
            $active={paymentMethodSelected === method}
            onClick={() => onSelectPaymentMethod(method)}
          >
            <PaymentMethodIcon $active={paymentMethodSelected === method}>
              <Icon />
            </PaymentMethodIcon>

            {label}
          </PaymentMethodOption>
        ))}
      </PaymentMethodOptions>

      {paymentMethodSelected === 'credit_card' && (
        <SectionBlock>
          <FormRow className="two-columns">
            <div className="form-group">
              <label htmlFor="payment-card-holder-name">
                Nome impresso no cartão <span className="text-danger">*</span>
              </label>

              <input
                id="payment-card-holder-name"
                name="cardHolderName"
                type="text"
                autoComplete="cc-name"
                className={showCardHolderNameError ? 'is-invalid' : undefined}
                aria-required="true"
                aria-invalid={showCardHolderNameError}
                aria-describedby={
                  showCardHolderNameError
                    ? 'payment-card-holder-name-error'
                    : undefined
                }
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values.cardHolderName}
              />

              {showCardHolderNameError && (
                <span
                  id="payment-card-holder-name-error"
                  className="invalid-feedback"
                  role="alert"
                >
                  {form.errors.cardHolderName}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment-card-number">
                Número do cartão <span className="text-danger">*</span>
              </label>

              <CardNumberFieldWrapper>
                <InputMask
                  id="payment-card-number"
                  name="cardNumber"
                  placeholder="0000 0000 0000 0000"
                  mask="9999 9999 9999 9999"
                  maskChar={null}
                  autoComplete="cc-number"
                  className={showCardNumberError ? 'is-invalid' : undefined}
                  aria-required="true"
                  aria-invalid={showCardNumberError}
                  aria-describedby={
                    showCardNumberError
                      ? 'payment-card-number-error'
                      : undefined
                  }
                  type="tel"
                  onChange={form.handleChange}
                  onBlur={form.handleBlur}
                  value={form.values.cardNumber}
                />

                <CardBrandIcon
                  src={toAbsoluteUrl(cardBrandSvg)}
                  alt=""
                  aria-hidden="true"
                />
              </CardNumberFieldWrapper>

              {showCardNumberError && (
                <span
                  id="payment-card-number-error"
                  className="invalid-feedback"
                  role="alert"
                >
                  {form.errors.cardNumber}
                </span>
              )}
            </div>
          </FormRow>

          <FormRow className="two-columns">
            <div className="form-group">
              <label htmlFor="payment-card-expiration">
                Vencimento (MM/AA) <span className="text-danger">*</span>
              </label>

              <InputMask
                id="payment-card-expiration"
                name="cardExpiration"
                placeholder="MM / AA"
                mask="99 / 99"
                maskChar={null}
                autoComplete="cc-exp"
                className={showCardExpirationError ? 'is-invalid' : undefined}
                aria-required="true"
                aria-invalid={showCardExpirationError}
                aria-describedby={
                  showCardExpirationError
                    ? 'payment-card-expiration-error'
                    : undefined
                }
                type="tel"
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values.cardExpiration}
              />

              {showCardExpirationError && (
                <span
                  id="payment-card-expiration-error"
                  className="invalid-feedback"
                  role="alert"
                >
                  {form.errors.cardExpiration}
                </span>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="payment-card-cvv">
                CVV <span className="text-danger">*</span>
              </label>

              <InputMask
                id="payment-card-cvv"
                name="cardCvv"
                placeholder="000"
                mask="9999"
                maskChar={null}
                autoComplete="cc-csc"
                className={showCardCvvError ? 'is-invalid' : undefined}
                aria-required="true"
                aria-invalid={showCardCvvError}
                aria-describedby={
                  showCardCvvError ? 'payment-card-cvv-error' : undefined
                }
                type="tel"
                onChange={form.handleChange}
                onBlur={form.handleBlur}
                value={form.values.cardCvv}
              />

              {showCardCvvError && (
                <span
                  id="payment-card-cvv-error"
                  className="invalid-feedback"
                  role="alert"
                >
                  {form.errors.cardCvv}
                </span>
              )}
            </div>
          </FormRow>
        </SectionBlock>
      )}
    </Card>
  );
};
